import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { BookOpen as A1, PlayCircle as A2, Award as A3, Brain as A4, Clock as A5, CheckCircle as A6 } from 'lucide-react';

import { useFocusMode } from '../context/FocusModeContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BookOpen, Youtube, Award, Users, ExternalLink, Search, Sparkles,
  GraduationCap, Briefcase, Play, FileText, Globe, Star, ArrowRight,
  ShoppingCart, DollarSign, Focus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Affiliate link generators (replace with actual affiliate IDs in production)
const getAmazonLink = (title, author) => 
  `https://www.amazon.com/s?k=${encodeURIComponent(title + ' ' + author)}&tag=futurepath-20`;

const getUdemyLink = (course) => 
  `https://www.udemy.com/courses/search/?q=${encodeURIComponent(course)}`;

const getCourseraLink = (course) => 
  `https://www.coursera.org/search?query=${encodeURIComponent(course)}`;

const getLinkedInLearningLink = (course) => 
  `https://www.linkedin.com/learning/search?keywords=${encodeURIComponent(course)}`;

// Book cover from Open Library
const getBookCover = (title) => 
  `https://covers.openlibrary.org/b/title/${encodeURIComponent(title)}-M.jpg`;

export default function LearnPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const { focusModeEnabled, focusedPathType, focusedPathName, getSuggestedCareers, getCurrentPathConfig } = useFocusMode();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [resources, setResources] = useState(null);

  // Get path config for styling
  const pathConfig = getCurrentPathConfig();
  const suggestedCareers = getSuggestedCareers();

  useEffect(() => {
    // If focus mode is enabled, pre-populate with first suggested career
    if (focusModeEnabled && suggestedCareers && suggestedCareers.length > 0) {
      setSearchQuery(suggestedCareers[0]);
    } else {
      loadSavedResources();
    }
  }, [focusModeEnabled, suggestedCareers]);

  const loadSavedResources = async () => {
    try {
      const response = await axios.get(`${API}/user-data`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      const data = response.data;
      if (data.pathways?.data?.paths?.[0]) {
        const firstPath = data.pathways.data.paths[0];
        setSearchQuery(firstPath.title || '');
      }
    } catch (error) {
      console.error('Failed to load resources:', error);
    }
  };

  const searchResources = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a career interest');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai/learning-resources?career_interest=${encodeURIComponent(searchQuery)}`,
        {},
        { ...getAuthHeader(), withCredentials: true, timeout: 60000 }
      );
      setResources(enrichResources(response.data.result, searchQuery));
      toast.success('Resources found!');
    } catch (error) {
      setResources(enrichResources(getFallbackResources(searchQuery), searchQuery));
      toast.success('Resources loaded!');
    } finally {
      setLoading(false);
    }
  };

  // Enrich resources with real links and images
  const enrichResources = (data, career) => {
    const careerSlug = career.toLowerCase().replace(/\s+/g, '-');
    
    return {
      ...data,
      books: data.books?.map(book => ({
        ...book,
        cover: getBookCover(book.title),
        amazonLink: getAmazonLink(book.title, book.author),
        price: book.price || '$15-30'
      })),
      online_courses: data.online_courses?.map(course => ({
        ...course,
        directLink: course.platform === 'Coursera' ? getCourseraLink(course.name) :
                    course.platform === 'LinkedIn Learning' ? getLinkedInLearningLink(course.name) :
                    getUdemyLink(course.name),
        logo: course.platform === 'Coursera' ? 'https://d3njjcbhbojbot.cloudfront.net/web/images/favicons/favicon-v2-194x194.png' :
              course.platform === 'LinkedIn Learning' ? 'https://static.licdn.com/sc/h/al2o9zrvru7aqj8e1x2rzsrca' :
              course.platform === 'Udemy' ? 'https://www.udemy.com/staticx/udemy/images/v7/logo-udemy.svg' :
              'https://via.placeholder.com/40'
      })),
      youtube_videos: getYouTubeVideos(career),
      influencers: data.influencers?.map(inf => ({
        ...inf,
        profileUrl: inf.platform === 'LinkedIn' ? `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(inf.name)}` :
                    inf.platform === 'Twitter' ? `https://twitter.com/search?q=${encodeURIComponent(inf.name)}` :
                    inf.platform === 'Instagram' ? `https://www.instagram.com/explore/tags/${careerSlug}/` :
                    inf.platform === 'YouTube' ? `https://www.youtube.com/results?search_query=${encodeURIComponent(inf.name)}` :
                    '#',
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(inf.name)}&background=random&size=80`
      }))
    };
  };

  // Get YouTube video IDs for embedding
  const getYouTubeVideos = (career) => {
    const careerVideos = {
      'software engineering': [
        { id: 'zOjov-2OZ0E', title: 'How to Become a Software Engineer', channel: 'TechLead' },
        { id: 'Uo3cL4nrGOk', title: 'Day in the Life of a Software Engineer', channel: 'Joma Tech' },
        { id: 'Z9Gd-bFd-FE', title: 'Software Engineering Career Path', channel: 'Fireship' }
      ],
      'ai': [
        { id: 'aircAruvnKk', title: 'AI Full Course for Beginners', channel: 'Edureka' },
        { id: 'JMUxmLyrhSk', title: 'Artificial Intelligence Explained', channel: 'IBM Technology' },
        { id: 'ad79nYk2keg', title: 'How to Get Started in AI', channel: 'Two Minute Papers' }
      ],
      'healthcare': [
        { id: 'YQZ2UeOTO3I', title: 'Healthcare Career Paths Explained', channel: 'Med School Insiders' },
        { id: 'cRxDsY8lF2o', title: 'Day in the Life of a Doctor', channel: 'Kevin Jubbal, M.D.' },
        { id: '7Ow9yKZ7zqU', title: 'Nursing Career Guide', channel: 'RegisteredNurseRN' }
      ],
      'business': [
        { id: 'Ihs4VFZWwn4', title: 'Business Career Paths', channel: 'The Finance Storyteller' },
        { id: 'ukzFI9rgwfU', title: 'How to Start a Business', channel: 'Valuetainment' },
        { id: 'SlCRfTmBSGs', title: 'MBA Worth It?', channel: 'CNBC' }
      ],
      'creative': [
        { id: 'owGykVbfgUE', title: 'Creative Career Paths', channel: 'The Futur' },
        { id: '2CmN0RxYHyE', title: 'How to Become a Designer', channel: 'Flux Academy' },
        { id: 'GYkq9Rgoj8E', title: 'Creative Industry Careers', channel: 'Will Paterson' }
      ],
      'default': [
        { id: 'Y7Xr3_TG_2Q', title: 'Finding Your Career Path', channel: 'TEDx Talks' },
        { id: 'C72WkcUZvco', title: 'How to Choose a Career', channel: 'Thomas Frank' },
        { id: 'MnmxYCl9jP8', title: 'Career Planning Guide', channel: 'Indeed' }
      ]
    };
    
    const careerLower = career.toLowerCase();
    if (careerLower.includes('software') || careerLower.includes('engineer') || careerLower.includes('developer')) {
      return careerVideos['software engineering'];
    } else if (careerLower.includes('ai') || careerLower.includes('machine learning') || careerLower.includes('artificial')) {
      return careerVideos['ai'];
    } else if (careerLower.includes('health') || careerLower.includes('medical') || careerLower.includes('nurse') || careerLower.includes('doctor')) {
      return careerVideos['healthcare'];
    } else if (careerLower.includes('business') || careerLower.includes('finance') || careerLower.includes('marketing')) {
      return careerVideos['business'];
    } else if (careerLower.includes('creative') || careerLower.includes('design') || careerLower.includes('art')) {
      return careerVideos['creative'];
    }
    return careerVideos['default'];
  };

  // Career-specific book recommendations
  const getCareerBooks = (career) => {
    const careerLower = career.toLowerCase();
    
    // Tech/Software books
    if (careerLower.includes('software') || careerLower.includes('developer') || careerLower.includes('engineer') || careerLower.includes('tech')) {
      return [
        { title: "Clean Code", author: "Robert C. Martin", description: "A handbook of agile software craftsmanship", level: "intermediate", price: "$34.99" },
        { title: "The Pragmatic Programmer", author: "David Thomas", description: "Your journey to mastery", level: "intermediate", price: "$49.99" },
        { title: "Cracking the Coding Interview", author: "Gayle McDowell", description: "189 programming questions & solutions", level: "beginner", price: "$26.99" }
      ];
    }
    
    // Healthcare books
    if (careerLower.includes('health') || careerLower.includes('medical') || careerLower.includes('nurse') || careerLower.includes('doctor')) {
      return [
        { title: "Being Mortal", author: "Atul Gawande", description: "Medicine and what matters in the end", level: "beginner", price: "$16.00" },
        { title: "The House of God", author: "Samuel Shem", description: "Classic novel about medical residency", level: "beginner", price: "$17.00" },
        { title: "Complications", author: "Atul Gawande", description: "A surgeon's notes on an imperfect science", level: "intermediate", price: "$16.99" }
      ];
    }
    
    // Trades/Construction books
    if (careerLower.includes('electric') || careerLower.includes('plumb') || careerLower.includes('hvac') || careerLower.includes('trade') || careerLower.includes('weld') || careerLower.includes('carpenter')) {
      return [
        { title: "Ugly's Electrical References", author: "George V. Hart", description: "Essential pocket guide for electricians", level: "beginner", price: "$21.95" },
        { title: "Carpentry and Building Construction", author: "John Feirer", description: "Complete guide to the trade", level: "intermediate", price: "$89.00" },
        { title: "Shop Class as Soulcraft", author: "Matthew Crawford", description: "Why working with your hands is fulfilling", level: "beginner", price: "$15.99" }
      ];
    }
    
    // Business books
    if (careerLower.includes('business') || careerLower.includes('finance') || careerLower.includes('marketing') || careerLower.includes('entrepreneur')) {
      return [
        { title: "The Lean Startup", author: "Eric Ries", description: "How to build a successful business", level: "beginner", price: "$16.99" },
        { title: "Zero to One", author: "Peter Thiel", description: "Notes on startups, or how to build the future", level: "intermediate", price: "$18.00" },
        { title: "Good to Great", author: "Jim Collins", description: "Why some companies make the leap", level: "intermediate", price: "$19.99" }
      ];
    }
    
    // AI/Data Science books  
    if (careerLower.includes('ai') || careerLower.includes('machine learning') || careerLower.includes('data') || careerLower.includes('artificial')) {
      return [
        { title: "Hands-On Machine Learning", author: "Aurélien Géron", description: "Scikit-Learn, Keras & TensorFlow", level: "intermediate", price: "$59.99" },
        { title: "Deep Learning", author: "Ian Goodfellow", description: "The MIT Press essential textbook", level: "advanced", price: "$72.00" },
        { title: "Python for Data Analysis", author: "Wes McKinney", description: "Data wrangling with pandas, NumPy", level: "beginner", price: "$44.99" }
      ];
    }
    
    // Creative/Design books
    if (careerLower.includes('creative') || careerLower.includes('design') || careerLower.includes('art') || careerLower.includes('content')) {
      return [
        { title: "Steal Like an Artist", author: "Austin Kleon", description: "10 things nobody told you about being creative", level: "beginner", price: "$12.99" },
        { title: "The Design of Everyday Things", author: "Don Norman", description: "Understanding good and bad design", level: "intermediate", price: "$17.99" },
        { title: "Show Your Work!", author: "Austin Kleon", description: "10 ways to share your creativity", level: "beginner", price: "$13.99" }
      ];
    }
    
    // Military books
    if (careerLower.includes('military') || careerLower.includes('army') || careerLower.includes('navy') || careerLower.includes('marine')) {
      return [
        { title: "Extreme Ownership", author: "Jocko Willink", description: "How U.S. Navy SEALs lead and win", level: "beginner", price: "$17.99" },
        { title: "Make Your Bed", author: "Admiral William H. McRaven", description: "Little things that can change your life", level: "beginner", price: "$14.99" },
        { title: "Leaders Eat Last", author: "Simon Sinek", description: "Why some teams pull together and others don't", level: "intermediate", price: "$17.00" }
      ];
    }
    
    // Default/General books
    return [
      { title: "Atomic Habits", author: "James Clear", description: "Build good habits for career success", level: "beginner", price: "$16.99" },
      { title: "Deep Work", author: "Cal Newport", description: "Rules for focused success in a distracted world", level: "intermediate", price: "$18.00" },
      { title: "The 7 Habits of Highly Effective People", author: "Stephen Covey", description: "Timeless principles for personal effectiveness", level: "beginner", price: "$17.99" }
    ];
  };

  const getFallbackResources = (career) => ({
    career_overview: {
      title: career,
      description: `Explore the exciting world of ${career}. This field offers diverse opportunities for growth and impact.`,
      average_salary: "$50,000 - $120,000",
      growth_outlook: "Growing",
      education_required: "Varies by specialization"
    },
    books: getCareerBooks(career),
    certifications: [
      { name: "Google Career Certificate", provider: "Google", cost: "$39/month", duration: "3-6 months", value: "Industry-recognized credential", link: "https://grow.google/certificates/" },
      { name: "Professional Certificate", provider: "Coursera", cost: "$49/month", duration: "4-6 months", value: "Demonstrates expertise", link: "https://www.coursera.org/professional-certificates" }
    ],
    online_courses: [
      { name: `${career} Fundamentals`, platform: "Coursera", cost: "Free", duration: "20 hours" },
      { name: `Complete ${career} Bootcamp`, platform: "Udemy", cost: "$19.99", duration: "40 hours" },
      { name: `${career} Professional Certificate`, platform: "LinkedIn Learning", cost: "$29.99/month", duration: "25 hours" }
    ],
    youtube_channels: [
      { name: "Ali Abdaal", description: "Productivity and career advice", subscriber_count: "4M+", channelUrl: "https://www.youtube.com/@aliabdaal" },
      { name: "Matt D'Avella", description: "Self-improvement and career growth", subscriber_count: "3.5M+", channelUrl: "https://www.youtube.com/@mattdavella" }
    ],
    influencers: [
      { name: "Simon Sinek", platform: "LinkedIn", why_follow: "Leadership and career inspiration" },
      { name: "Gary Vaynerchuk", platform: "Instagram", why_follow: "Entrepreneurship and hustle mindset" },
      { name: "Naval Ravikant", platform: "Twitter", why_follow: "Wealth and career wisdom" }
    ],
    local_opportunities: [
      { type: "internship", name: "Summer Internship Programs", description: "Hands-on experience with local companies" },
      { type: "meetup", name: "Professional Networking Events", description: "Connect with others in the field" }
    ],
    next_steps: [
      "Watch the introductory videos below",
      "Pick one book and start reading",
      "Enroll in a free online course",
      "Follow industry leaders on social media",
      "Join local meetups or online communities"
    ]
  });

  const suggestedDefaultCareers = [
    "Software Engineering",
    "Healthcare",
    "Business & Finance",
    "Creative Arts",
    "AI & Machine Learning",
    "Data Science"
  ];

  // Use focus mode suggestions or default
  const displaySuggestions = focusModeEnabled && suggestedCareers ? suggestedCareers : suggestedDefaultCareers;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="learn-arrival"
        eyebrow="Learning Portal"
        title="Learn the thing, then prove it."
        subtitle="Short, practical lessons tied to the gaps in your own plan, each one finishing with something you can point to rather than a feeling that you have learned."
        accent="#a78bfa"
        second="#22d3ee"
        ctaLabel="Start learning"
        narration={"This is your Learning Portal. Nothing here is filler: the lessons are chosen because of a gap in your own plan, and each finishes with something you can point at, a skill added, a document written, a step completed. Short enough to finish in a sitting, and everything you complete lands in your record as evidence."}
        facts={[
          { icon: A5, head: "Short lessons", sub: "Finish one in a sitting" },
          { icon: A4, head: "Chosen for you", sub: "Tied to gaps in your own plan" },
          { icon: A6, head: "Becomes evidence", sub: "Completions land in your record" }
        ]}
        modules={[
          { icon: A1, label: "Lessons", line: "Practical, plain, and about your situation", hue: "#a78bfa" },
          { icon: A2, label: "Walkthroughs", line: "Watch it done before you do it", hue: "#22d3ee" },
          { icon: A4, label: "Skill builders", line: "Exercises that produce real proof", hue: "#34d399" },
          { icon: A3, label: "Certificates", line: "What you finish, recorded and shareable", hue: "#fbbf24" },
          { icon: A5, label: "Track progress", line: "Where you left off, always", hue: "#f472b6" },
          { icon: A6, label: "Next lesson", line: "Chosen from what your plan needs next", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title={focusModeEnabled ? `${focusedPathName} Learning` : "Learning Portal"}
        description={focusModeEnabled 
          ? `Discover resources to accelerate your ${focusedPathName?.toLowerCase()} journey`
          : "Discover resources to accelerate your career journey"}
      />

      {/* Focus Mode Banner */}
      {focusModeEnabled && pathConfig && (
        <div className={`mt-6 mb-4 p-4 rounded-xl bg-gradient-to-r ${pathConfig.gradient} relative overflow-hidden`}>
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Focus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                <span className="text-xl">{pathConfig.icon}</span>
                Learning Resources for {focusedPathName}
              </h3>
              <p className="text-white/80 text-sm">
                Books, courses, and videos filtered to your focus path
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Section */}
      <GlassCard className="mt-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-outfit text-xl font-semibold text-white">Find Learning Resources</h2>
            <p className="text-slate-300 text-sm">
              {focusModeEnabled 
                ? `Explore ${focusedPathName?.toLowerCase()} resources - books, courses, videos, and more`
                : "Enter your career interest to discover books, courses, videos, and more"}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={focusModeEnabled 
              ? `e.g., ${suggestedCareers?.[0] || 'Career interest'}...`
              : "e.g., AI Music Creator, Software Engineer, Healthcare..."}
            className="flex-1 bg-slate-800 border-slate-700 text-white"
            onKeyPress={(e) => e.key === 'Enter' && searchResources()}
            data-testid="career-search-input"
          />
          <Button
            onClick={searchResources}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="search-resources-btn"
          >
            {loading ? <LoadingSpinner size="sm" /> : <Search className="w-5 h-5 mr-2" />}
            Find Resources
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <span className="text-slate-300 text-sm">
            {focusModeEnabled ? `${focusedPathName} topics:` : 'Suggestions:'}
          </span>
          {displaySuggestions.map((career) => (
            <button
              key={career}
              onClick={() => {
                setSearchQuery(career);
                // Auto-search when clicking a suggestion
                setTimeout(() => searchResources(), 100);
              }}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                focusModeEnabled 
                  ? 'bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-cyan-500/30'
                  : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
              }`}
            >
              {career}
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Resources Display */}
      {resources && (
        <div className="space-y-6 mt-6">
          {/* Career Overview */}
          <GlassCard>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h2 className="font-outfit text-2xl font-bold text-white">{resources.career_overview?.title}</h2>
                <p className="text-slate-300 mt-2">{resources.career_overview?.description}</p>
                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="px-4 py-2 bg-emerald-500/20 rounded-lg">
                    <p className="text-emerald-400 text-sm font-medium">Average Salary</p>
                    <p className="text-white">{resources.career_overview?.average_salary}</p>
                  </div>
                  <div className="px-4 py-2 bg-blue-500/20 rounded-lg">
                    <p className="text-blue-400 text-sm font-medium">Growth Outlook</p>
                    <p className="text-white">{resources.career_overview?.growth_outlook}</p>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* YouTube Videos - Embedded */}
          {resources.youtube_videos?.length > 0 && (
            <GlassCard>
              <div className="flex items-center gap-3 mb-4">
                <Youtube className="w-6 h-6 text-red-500" />
                <h3 className="font-outfit text-lg font-semibold text-white">Watch & Learn</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {resources.youtube_videos.map((video, i) => (
                  <div key={i} className="rounded-lg overflow-hidden bg-slate-800/50">
                    <div className="aspect-video">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-white text-sm font-medium line-clamp-2">{video.title}</p>
                      <p className="text-slate-300 text-xs mt-1">{video.channel}</p>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Books with Covers and Buy Links */}
          {resources.books?.length > 0 && (
            <GlassCard>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-amber-400" />
                  <h3 className="font-outfit text-lg font-semibold text-white">Recommended Books</h3>
                </div>
                <span className="text-xs text-slate-300 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Affiliate links support FuturePath
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {resources.books.map((book, i) => (
                  <a 
                    key={i}
                    href={book.amazonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex gap-4 p-4 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="w-20 h-28 flex-shrink-0 bg-slate-700 rounded overflow-hidden">
                      <img 
                        src={book.cover}
                        alt={book.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/80x112/334155/94a3b8?text=${encodeURIComponent(book.title.substring(0,10))}`;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium group-hover:text-amber-400 transition-colors line-clamp-2">
                        {book.title}
                      </h4>
                      <p className="text-slate-300 text-sm">{book.author}</p>
                      <p className="text-slate-300 text-xs mt-1 line-clamp-2">{book.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-emerald-400 text-sm font-medium">{book.price}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          book.level === 'beginner' ? 'bg-emerald-500/20 text-emerald-400' :
                          book.level === 'intermediate' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {book.level}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-2 text-amber-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                        <ShoppingCart className="w-3 h-3" />
                        Buy on Amazon
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Online Courses with Platform Logos */}
          {resources.online_courses?.length > 0 && (
            <GlassCard>
              <div className="flex items-center gap-3 mb-4">
                <Play className="w-6 h-6 text-blue-400" />
                <h3 className="font-outfit text-lg font-semibold text-white">Online Courses</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {resources.online_courses.map((course, i) => (
                  <a
                    key={i}
                    href={course.directLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group p-4 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors border border-slate-700/50 hover:border-blue-500/50"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img 
                        src={course.logo}
                        alt={course.platform}
                        className="w-8 h-8 rounded"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/32/334155/94a3b8?text=C';
                        }}
                      />
                      <span className="text-slate-300 text-sm">{course.platform}</span>
                    </div>
                    <h4 className="text-white font-medium group-hover:text-blue-400 transition-colors">
                      {course.name}
                    </h4>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-emerald-400 font-medium">{course.cost}</span>
                      <span className="text-slate-300 text-sm">{course.duration}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-3 text-blue-400 text-sm">
                      <ExternalLink className="w-4 h-4" />
                      <span className="group-hover:underline">Start Learning</span>
                    </div>
                  </a>
                ))}
              </div>
            </GlassCard>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Certifications */}
            {resources.certifications?.length > 0 && (
              <GlassCard>
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-6 h-6 text-purple-400" />
                  <h3 className="font-outfit text-lg font-semibold text-white">Certifications</h3>
                </div>
                <div className="space-y-3">
                  {resources.certifications.map((cert, i) => (
                    <a 
                      key={i}
                      href={cert.link || `https://www.google.com/search?q=${encodeURIComponent(cert.name + ' ' + cert.provider)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-white font-medium group-hover:text-purple-400">{cert.name}</h4>
                        <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-purple-400" />
                      </div>
                      <p className="text-slate-300 text-sm">{cert.provider}</p>
                      <div className="flex gap-3 mt-2 text-sm">
                        <span className="text-emerald-400">{cert.cost}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-blue-400">{cert.duration}</span>
                      </div>
                      <p className="text-slate-300 text-sm mt-2">{cert.value}</p>
                    </a>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* People to Follow with Profile Pictures */}
            <GlassCard>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-pink-400" />
                <h3 className="font-outfit text-lg font-semibold text-white">People to Follow</h3>
              </div>
              
              {/* YouTube Channels */}
              {resources.youtube_channels?.length > 0 && (
                <div className="mb-4">
                  <p className="text-slate-300 text-xs uppercase mb-3">YouTube Channels</p>
                  <div className="space-y-2">
                    {resources.youtube_channels.map((channel, i) => (
                      <a 
                        key={i}
                        href={channel.channelUrl || `https://www.youtube.com/results?search_query=${encodeURIComponent(channel.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors group"
                      >
                        <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                          <Youtube className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium group-hover:text-red-400">{channel.name}</p>
                          <p className="text-slate-300 text-xs">{channel.subscriber_count} subscribers</p>
                          <p className="text-slate-300 text-xs">{channel.description}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-red-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Influencers */}
              {resources.influencers?.length > 0 && (
                <div>
                  <p className="text-slate-300 text-xs uppercase mb-3">Industry Leaders</p>
                  <div className="grid grid-cols-1 gap-2">
                    {resources.influencers.map((inf, i) => (
                      <a 
                        key={i}
                        href={inf.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors group"
                      >
                        <img 
                          src={inf.avatar}
                          alt={inf.name}
                          className="w-12 h-12 rounded-full"
                        />
                        <div className="flex-1">
                          <p className="text-white font-medium group-hover:text-blue-400">{inf.name}</p>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              inf.platform === 'LinkedIn' ? 'bg-blue-600/20 text-blue-400' :
                              inf.platform === 'Twitter' ? 'bg-sky-500/20 text-sky-400' :
                              inf.platform === 'Instagram' ? 'bg-pink-500/20 text-pink-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {inf.platform}
                            </span>
                          </div>
                          <p className="text-slate-300 text-xs mt-1">{inf.why_follow}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-blue-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </GlassCard>
          </div>

          {/* Next Steps */}
          {resources.next_steps?.length > 0 && (
            <GlassCard>
              <div className="flex items-center gap-3 mb-4">
                <ArrowRight className="w-6 h-6 text-emerald-400" />
                <h3 className="font-outfit text-lg font-semibold text-white">Your Next Steps</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {resources.next_steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-slate-300 text-sm">{step}</p>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Affiliate Disclosure */}
          <div className="text-center text-xs text-slate-300 py-4">
            <p>Some links are affiliate links. When you purchase through these links, FuturePath may earn a commission at no extra cost to you.</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!resources && !loading && (
        <GlassCard className="mt-6 text-center py-16">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="font-outfit text-xl font-semibold text-white mb-2">Discover Your Learning Path</h3>
          <p className="text-slate-300 mb-6 max-w-md mx-auto">
            Enter a career interest above to get personalized book recommendations, 
            online courses, videos, and people to follow.
          </p>
        </GlassCard>
      )}

    </AppLayout>
  );
}
